import React from "react";
import { useWatchStore } from "../stores/watchStore.js";
import { useWardrobeStore } from "../stores/wardrobeStore.js";
import { useThemeStore } from "../stores/themeStore.js";
import { useAuthStore } from "../stores/authStore.js";
import { isActiveWatch } from "../utils/watchFilters.js";
import { GitHubLoginButton } from "./GitHubLoginButton.jsx";

export default function Header({ onOpenSettings, onOpenSearch }) {
  const watches = useWatchStore(s => s.watches) ?? [];
  const garments = useWardrobeStore(s => s.garments) ?? [];
  const { mode, toggle } = useThemeStore();
  const isDark = mode === "dark";
  const isAuthed = useAuthStore(s => s.isAuthed);
  const authInitialized = useAuthStore(s => s._initialized);
  const activeGarmentCount = garments.filter(g => !g.excludeFromWardrobe).length;
  // Show the sign-in hint only AFTER the initial session check has completed
  // — otherwise we'd flash "Sign in" for ~50ms on a real authed reload before
  // getSession() resolves and the IDB cache hydrates the garments store.
  const showSignInHint = authInitialized && !isAuthed && activeGarmentCount === 0;

  const btnStyle = {
    padding: "11px 14px", borderRadius: 8, border: `1px solid ${isDark ? "#2b3140" : "#d1d5db"}`,
    background: isDark ? "#0f131a" : "#f9fafb", color: isDark ? "#e2e8f0" : "#1f2937",
    fontSize: 14, cursor: "pointer", minHeight: 44, minWidth: 44,
  };

  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      marginBottom: 20, paddingBottom: 16,
      borderBottom: `1px solid ${isDark ? "#2b3140" : "#d1d5db"}`,
      flexWrap: "wrap", gap: 10,
    }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em" }}>
          watch-advisor
        </h1>
        <div style={{ color: isDark ? "#9ca3af" : "#6b7280", fontSize: 13, marginTop: 2 }}>
          {showSignInHint ? (
            <>Watch-first outfit planner &middot; {watches.filter(isActiveWatch).length} watches &middot; <span style={{ color: isDark ? "#fbbf24" : "#92400e", fontWeight: 600 }}>Sign in to load your wardrobe</span> &middot; <span style={{ color: isDark ? "#9ca3af" : "#6b7280" }}>v{__BUILD_NUMBER__}</span></>
          ) : (
            <>Watch-first outfit planner &middot; {watches.filter(isActiveWatch).length} watches &middot; {activeGarmentCount} garments &middot; <span style={{ color: isDark ? "#9ca3af" : "#6b7280" }}>v{__BUILD_NUMBER__}</span></>
          )}
        </div>
      </div>
      <div className="wa-header-actions" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* GitHub auth — required when AUTH_GATE_ENABLED=true on the server.
            Visible by default so the user always knows their sign-in state. */}
        <GitHubLoginButton />
        {/* Search / Command Palette */}
        <button
          onClick={onOpenSearch}
          title="Search (Ctrl+K)"
          style={{ ...btnStyle, display: "flex", alignItems: "center", gap: 6 }}
        >
          &#128269;
          <span style={{ fontSize: 11, color: isDark ? "#9ca3af" : "#6b7280" }}>Ctrl+K</span>
        </button>
        {/* Day/Night toggle */}
        <button
          onClick={toggle}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          style={{ ...btnStyle, display: "flex", alignItems: "center", gap: 5 }}
        >
          {isDark ? "\u263E" : "\u2600"} {isDark ? "Night" : "Day"}
        </button>
        {/* Settings */}
        <button onClick={onOpenSettings} title="Settings" aria-label="Settings" style={btnStyle}>
          &#9881;
        </button>
      </div>
    </div>
  );
}
